import { FunctionComponent } from 'react';
import { Avatar, Flex, Icon, IconButton, Menu, MenuButton, MenuItem, MenuList, Td, Text, useToken } from '@chakra-ui/react';
import { Reorder, useDragControls } from 'framer-motion';
import { EllipsisVerticalIcon } from '@heroicons/react/24/outline';
import { useTranslation } from 'react-i18next';
import { RosterMember } from '@/model';
import { ChakraFeatherIcon } from '@/components/ChakraFeatherIcon';
import { DragHandle } from '@/pages/ApplicationChampionshipStageStandings/components/StandingsTable/DragHandle';

type RosterMemberRowProps = {
  item: RosterMember;
  isDragDisabled?: boolean;
  onEditClick: (item: RosterMember) => void;
  onDeleteClick: (item: RosterMember) => void;
};

export const RosterMemberRow: FunctionComponent<RosterMemberRowProps> = ({
  item,
  isDragDisabled = false,
  onEditClick,
  onDeleteClick,
}) => {
  const { t } = useTranslation();
  const dragControls = useDragControls();
  const [borderColor] = useToken('colors', ['gray.100']);

  const typeLabel =
    item.type === 'player'
      ? t('pages.application_roster_member.components.roster_member_table.roster_member_table_type_player')
      : t('pages.application_roster_member.components.roster_member_table.roster_member_table_type_staff');

  return (
    <Reorder.Item
      as='tr'
      value={item.uuid}
      dragListener={false}
      dragControls={dragControls}
      style={{
        background: 'white',
        borderTop: `1px solid ${borderColor}`,
        touchAction: 'none',
      }}
    >
      <Td px={2} w='32px'>
        <DragHandle
          isDisabled={isDragDisabled}
          onPointerDown={event => dragControls.start(event)}
          aria-label='drag'
        />
      </Td>

      <Td px={3}>
        <Text>{typeLabel}</Text>
      </Td>

      <Td px={3}>
        <Flex alignItems='center' gap={4}>
          <Avatar size='md' src={item.picture} name={item.name} bg='brand.50' color='white' />
          <Text>{item.name}</Text>
        </Flex>
      </Td>

      <Td px={3}>
        <Text>{item.role_label || ''}</Text>
      </Td>

      <Td px={3}>
        <Text>{item.group_name || ''}</Text>
      </Td>

      <Td px={3} textAlign='center'>
        <Text>{item.shirt_number || ''}</Text>
      </Td>

      <Td px={3}>
        <Text>{item.created_at}</Text>
      </Td>

      <Td px={2} textAlign='right'>
        <Flex justifyContent='end'>
          <Menu>
            <MenuButton
              textColor='text.table'
              as={IconButton}
              variant='ghost'
              size='sm'
              icon={<Icon as={EllipsisVerticalIcon} />}
              aria-label={t('pages.application_roster_member.components.roster_member_table.roster_member_table_show_more_actions')}
            />
            <MenuList textColor='white'>
              <MenuItem
                onClick={() => onEditClick(item)}
                icon={
                  <Text>
                    <ChakraFeatherIcon name='eye' />
                  </Text>
                }
              >
                <Text>{t('pages.application_roster_member.components.roster_member_table.roster_member_table_view_details')}</Text>
              </MenuItem>
              <MenuItem
                onClick={() => onDeleteClick(item)}
                icon={
                  <Text>
                    <ChakraFeatherIcon name='trash-2' color='red.500' w='18px' />
                  </Text>
                }
              >
                <Text>{t('pages.application_roster_member.components.roster_member_table.roster_member_table_delete')}</Text>
              </MenuItem>
            </MenuList>
          </Menu>
        </Flex>
      </Td>
    </Reorder.Item>
  );
};
